import { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate, useParams, matchPath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PaginatedVenuesResponse } from '@lov/shared';
import type { VenueResponse } from '../features/venues/hooks/useVenues';
import { validatedApi } from '../lib/api/validated-client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { VenueSwitcherDropdown } from './VenueSwitcherDropdown';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { styled, alpha } from '@mui/material/styles';
import {
  Dashboard,
  People,
  Store,
  Inventory2,
  Business,
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Storefront,
  AddBusiness,
  CalendarToday,
  Discount,
  Settings,
  Analytics,
  AccountBalance,
  Receipt,
  Star,
  EventNote,
  CardMembership,
  WorkspacePremium,
  Favorite,
  Tune,
  Build,
  SupportAgent,
  RestaurantMenu,
  Computer,
  EmojiEvents,
} from '@mui/icons-material';
import { Role, FEATURE_FLAGS } from '@lov/shared';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../features/feature-flags/hooks/useFeatureFlags';
import { LockedFeatureBadge } from '../features/vendor/entitlements';
import { useVendorSubscription } from '../features/vendor/subscription/hooks/useVendorSubscription';
import { useOvertimePending } from '../features/bookings/hooks/useOvertimeActions';

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  width: collapsed ? 56 : 240,
  transition: 'width 0.2s ease',
  height: 'calc(100vh - 72px)',
  backgroundColor: theme.palette.sidebar.bg,
  borderRight: 'none',
  position: 'sticky',
  top: 72,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1, 0),
  overflowY: collapsed ? 'hidden' : 'auto',
  overflowX: collapsed ? 'visible' : 'hidden',
  ...(collapsed
    ? {}
    : {
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255,255,255,0.03)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 3,
          '&:hover': {
            background: 'rgba(255,255,255,0.25)',
          },
        },
      }),
}));

const SidebarSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Poppins", sans-serif',
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: theme.palette.sidebar.sectionLabel,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  padding: theme.spacing(0.25, 2),
  marginBottom: theme.spacing(0.25),
}));

const NavItem = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'nested',
})<{ nested?: boolean }>(({ theme, nested }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(0.75, 1.75),
  marginLeft: nested ? theme.spacing(2) : theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  borderRadius: 8,
  textDecoration: 'none',
  color: theme.palette.sidebar.text,
  fontFamily: '"Poppins", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  position: 'relative',
  backgroundColor: 'transparent',

  '&:hover': {
    backgroundColor: theme.palette.sidebar.hover,
    color: theme.palette.sidebar.textActive,
  },

  '& svg': {
    fontSize: 20,
    transition: 'color 0.2s ease',
    flexShrink: 0,
  },

  // Active state — orange left-border indicator
  '&.active': {
    color: theme.palette.sidebar.textActive,
    fontWeight: 600,
    backgroundColor: theme.palette.sidebar.activeBg,

    '&:hover': {
      backgroundColor: alpha(theme.palette.sidebar.activeIndicator, 0.18),
    },

    '& svg': {
      color: theme.palette.sidebar.activeIndicator,
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      left: -4,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: 22,
      backgroundColor: theme.palette.sidebar.activeIndicator,
      borderRadius: '0 3px 3px 0',
      boxShadow: `0 0 8px ${alpha(theme.palette.sidebar.activeIndicator, 0.5)}`,
    },
  },
}));

const ExpandButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(0.75, 1.75),
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  borderRadius: 8,
  cursor: 'pointer',
  color: isActive ? theme.palette.sidebar.textActive : theme.palette.sidebar.text,
  fontFamily: '"Poppins", sans-serif',
  fontSize: '0.875rem',
  fontWeight: isActive ? 600 : 500,
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? theme.palette.sidebar.activeBg : 'transparent',
  position: 'relative',

  '&:hover': {
    backgroundColor: theme.palette.sidebar.hover,
    color: theme.palette.sidebar.textActive,
  },

  '& svg': {
    fontSize: 20,
    flexShrink: 0,
    color: isActive ? theme.palette.sidebar.activeIndicator : 'inherit',
  },
}));

interface NavSection {
  title: string;
  items: NavItemConfig[];
  roles: Role[];
}

interface NavItemConfig {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: { label: string; path: string; end?: boolean }[];
  disabled?: boolean;
  badge?: string;
  requiredPermission?: string; // permission string from StaffPermission enum
  locked?: boolean;
  requiredPlan?: string;
  featureFlag?: string;
}

const adminNavigation: NavSection = {
  title: 'Administration',
  roles: [Role.SUPER_ADMIN],
  items: [
    { label: 'Dashboard', path: '/admin/dashboard', icon: <Dashboard /> },
    { label: 'Bookings', path: '/admin/bookings', icon: <EventNote /> },
    {
      label: 'People',
      path: '/admin/users',
      icon: <People />,
      children: [
        { label: 'Customers', path: '/admin/users' },
        { label: 'Staff', path: '/admin/staff' },
      ],
    },
    {
      label: 'Vendors',
      path: '/admin/vendors',
      icon: <Business />,
      children: [
        { label: 'All Vendors', path: '/admin/vendors' },
        { label: 'Pending Approval', path: '/admin/vendors/pending' },
        { label: 'Venue Enquiries', path: '/admin/vendor-enquiries' },
      ],
    },
    {
      label: 'Venues',
      path: '/admin/venues',
      icon: <Store />,
      children: [
        { label: 'All Venues', path: '/admin/venues' },
        { label: 'Create Venue', path: '/admin/venues/create' },
        { label: 'Pending Approval', path: '/admin/venues/pending' },
        { label: 'Locations', path: '/admin/locations' },
      ],
    },
    {
      label: 'Inventory',
      path: '/admin/inventory',
      icon: <Inventory2 />,
      children: [
        { label: 'Catalog', path: '/admin/inventory' },
        { label: 'Requests', path: '/admin/inventory/requests' },
      ],
    },
    { label: 'Promotions', path: '/admin/coupons', icon: <Discount /> },
    { label: 'Support Tickets', path: '/admin/support', icon: <SupportAgent /> },
    { label: 'Analytics', path: '/admin/analytics', icon: <Analytics /> },
    {
      label: 'Billing & Finance',
      path: '/admin/payouts',
      icon: <AccountBalance />,
      children: [
        { label: 'Invoices', path: '/admin/invoices' },
        { label: 'Payouts', path: '/admin/payouts' },
        { label: 'Subscriptions', path: '/admin/subscriptions' },
        { label: 'Subscription Plans', path: '/admin/subscription-plans' },
      ],
    },
    {
      label: 'Platform Config',
      path: '/admin/modules',
      icon: <Tune />,
      children: [
        { label: 'Capability Modules', path: '/admin/modules' },
        { label: 'Amenities', path: '/admin/master-data?tab=1' },
        { label: 'Check-In Settings', path: '/admin/check-in-config' },
      ],
    },
    {
      label: 'System',
      path: '/admin/settings',
      icon: <Build />,
      children: [
        { label: 'Feature Flags', path: '/admin/feature-flags' },
        { label: 'Cron Monitoring', path: '/admin/cron-monitoring' },
        { label: 'SSL Certificates', path: '/admin/ssl' },
        { label: 'System Settings', path: '/admin/settings' },
      ],
    },
  ],
};

const vendorNavigation: NavSection = {
  title: 'Vendor Portal',
  roles: [Role.VENDOR, Role.VENDOR_STAFF],
  items: [
    { label: 'Dashboard', path: '/vendor/:venueId/dashboard', icon: <Dashboard /> },
    {
      label: 'Bookings',
      path: '/vendor/:venueId/bookings',
      icon: <CalendarToday />,
      children: [
        { label: 'All Bookings', path: '/vendor/:venueId/bookings', end: true },
        { label: 'Calendar', path: '/vendor/:venueId/bookings/calendar' },
        { label: 'Active Sessions', path: '/vendor/:venueId/bookings/active-sessions' },
        { label: 'Check-In', path: '/vendor/:venueId/check-in' },
        { label: 'Pending Payments', path: '/vendor/:venueId/unpaid-bookings' },
      ],
    },
    {
      label: 'Venue',
      path: '/vendor/venues/:venueId',
      icon: <Storefront />,
    },
    { label: 'Promotions', path: '/vendor/:venueId/coupons', icon: <Discount /> },
    {
      label: 'Food',
      path: '/vendor/:venueId/food-menu',
      icon: <RestaurantMenu />,
      requiredPermission: 'MANAGE_VENUE_DETAILS',
      children: [
        { label: 'Menu', path: '/vendor/:venueId/food-menu' },
        { label: 'Offers', path: '/vendor/:venueId/food-offers' },
      ],
    },
    {
      label: 'Analytics',
      path: '/vendor/:venueId/analytics',
      icon: <Analytics />,
      requiredPermission: 'VIEW_REVENUE',
      children: [
        { label: 'Overview', path: '/vendor/:venueId/analytics', end: true },
        { label: 'Add-on Performance', path: '/vendor/:venueId/addon-analytics' },
        { label: 'Food Analytics', path: '/vendor/:venueId/food-analytics' },
      ],
    },
    { label: 'Customers', path: '/vendor/:venueId/customers', icon: <People /> },
    {
      label: 'Team',
      path: '/vendor/:venueId/staff',
      icon: <People />,
      requiredPermission: 'VIEW_STAFF',
    },
    {
      label: 'Finance',
      path: '/vendor/:venueId/payouts',
      icon: <AccountBalance />,
      requiredPermission: 'VIEW_PAYOUTS',
      children: [
        { label: 'Payouts', path: '/vendor/:venueId/payouts' },
        { label: 'Tax Invoices', path: '/vendor/:venueId/tax-invoices' },
        { label: 'Commission Invoices', path: '/vendor/:venueId/commission-invoices' },
        { label: 'Tax Summary', path: '/vendor/:venueId/tax-summary' },
      ],
    },
    {
      label: 'Billing & Plans',
      path: '/vendor/subscription/plans',
      icon: <CardMembership />,
      requiredPermission: 'MANAGE_VENUE_DETAILS',
      locked: true,
      requiredPlan: 'BASIC',
      featureFlag: FEATURE_FLAGS.VENDOR_BILLING,
      children: [
        { label: 'Subscription Plans', path: '/vendor/subscription/plans' },
        { label: 'Billing History', path: '/vendor/billing-history' },
      ],
    },
    {
      label: 'PC Management',
      path: '/vendor/:venueId/pc-management/agent-devices',
      icon: <Computer />,
      requiredPermission: 'MANAGE_VENUE_DETAILS',
      children: [
        { label: 'Agent Devices', path: '/vendor/:venueId/pc-management/agent-devices' },
        { label: 'PC Status', path: '/vendor/:venueId/pc-management/pc-grid' },
      ],
    },
    {
      label: 'Leaderboard',
      path: '/vendor/:venueId/competitive',
      icon: <EmojiEvents />,
      featureFlag: FEATURE_FLAGS.COMPETITIVE_LEADERBOARD,
    },
    { label: 'Reviews', path: '/vendor/:venueId/reviews', icon: <Star /> },
    { label: 'Settings', path: '/vendor/settings', icon: <Settings /> },
  ],
};

const customerNavigation: NavSection = {
  title: 'Account',
  roles: [Role.CUSTOMER],
  items: [
    { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { label: 'Bookings', path: '/bookings', icon: <CalendarToday /> },
    { label: 'Invoices', path: '/invoices', icon: <Receipt /> },
    { label: 'Saved Venues', path: '/favorites', icon: <Favorite /> },
    { label: 'Browse Venues', path: '/venues', icon: <Store /> },
    { label: 'Register Your Venue', path: '/list-your-venue', icon: <AddBusiness /> },
  ],
};

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ collapsed = false, onToggleCollapse }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const isVendor = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;
  const { data: subscriptionData } = useVendorSubscription({ enabled: isVendor });
  const planName = subscriptionData?.plan?.name;

  // venueId from URL params — present on /vendor/:venueId/* routes, undefined on /vendor/venues etc.
  const { venueId: routeVenueId } = useParams<{ venueId: string }>();

  // Same cache key as VenueLayout — no extra network request, just reads from React Query cache
  const { data: venues } = useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: () => validatedApi.get('/venues/my'),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
    enabled: isVendor,
  });

  // Fall back to first venue so nav links remain usable on /vendor/venues and similar routes
  const venueId = routeVenueId ?? venues?.[0]?.id;
  const overtimeVenueId = isVendor && venueId ? venueId : undefined;
  const { data: overtimePending } = useOvertimePending(overtimeVenueId);
  const hasOvertimePending = (overtimePending?.length ?? 0) > 0;

  const { data: vendorBillingFlag } = useFeatureFlag(FEATURE_FLAGS.VENDOR_BILLING);
  const { data: competitiveFlag } = useFeatureFlag(FEATURE_FLAGS.COMPETITIVE_LEADERBOARD);

  // Memoize isParentActive callback to prevent recreating on every render
  const isParentActive = useCallback(
    (item: NavItemConfig) => {
      const patterns = [item.path, ...(item.children?.map((c) => c.path) ?? [])];
      return patterns.some((p) => !!matchPath({ path: p, end: false }, location.pathname));
    },
    [location.pathname]
  );

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  // Memoize sections to prevent recreating on every render
  const sections = useMemo((): NavSection[] => {
    if (!user?.role) return [];

    switch (user.role) {
      case Role.SUPER_ADMIN:
        return [adminNavigation];
      case Role.VENDOR:
      case Role.VENDOR_STAFF: {
        const flagStates: Record<string, boolean> = {
          [FEATURE_FLAGS.VENDOR_BILLING]: vendorBillingFlag?.enabled ?? false,
          [FEATURE_FLAGS.COMPETITIVE_LEADERBOARD]: competitiveFlag?.enabled ?? false,
        };

        let filteredItems = vendorNavigation.items.filter(
          (item) => !item.featureFlag || flagStates[item.featureFlag]
        );

        if (user.role === Role.VENDOR_STAFF) {
          filteredItems = filteredItems.filter((item) => !item.requiredPermission);
        }

        return [{ ...vendorNavigation, items: filteredItems }];
      }
      case Role.CUSTOMER:
        return [customerNavigation];
      default:
        return [];
    }
  }, [user?.role, vendorBillingFlag, competitiveFlag]);

  // Resolve :venueId param in nav path patterns to actual venueId
  const resolvePath = useCallback(
    (path: string) => {
      if (!path.includes(':venueId')) return path;
      return venueId ? path.replace(':venueId', venueId) : path;
    },
    [venueId]
  );

  // Calculate which sections should be expanded based on active route
  const activeExpandedItems = useMemo(() => {
    const expanded: string[] = [];
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children && isParentActive(item)) {
          expanded.push(item.label);
        }
      });
    });
    return expanded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sections, isParentActive]);

  // Auto-expand active sections on mount and route change
  useEffect(() => {
    setExpandedItems((prev) => {
      // Only update if activeExpandedItems has new items
      const hasNewItems = activeExpandedItems.some((item) => !prev.includes(item));
      if (!hasNewItems) return prev; // Prevent unnecessary state updates

      // Merge with existing expanded items to preserve user's manual expansions
      const merged = Array.from(new Set([...prev, ...activeExpandedItems]));
      return merged;
    });
  }, [activeExpandedItems]);

  return (
    <SidebarContainer collapsed={collapsed} role="navigation" aria-label="Vendor sidebar">
      {sections.map((section) => (
        <SidebarSection key={section.title}>
          {collapsed ? (
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.5 }} />
          ) : (
            <SectionTitle>{section.title}</SectionTitle>
          )}
          {!collapsed &&
            section.roles.some((r) => r === Role.VENDOR || r === Role.VENDOR_STAFF) && (
              <>
                <VenueSwitcherDropdown />
                {planName && (
                  <Box sx={{ px: 2, pb: 0.5 }}>
                    <Chip
                      icon={<WorkspacePremium sx={{ fontSize: '14px !important' }} />}
                      label={planName}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        bgcolor: 'rgba(255,136,0,0.15)',
                        color: '#FF8800',
                        border: '1px solid rgba(255,136,0,0.3)',
                        '& .MuiChip-icon': { color: '#FF8800' },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          {section.items.map((item) => (
            <Box key={item.label} sx={{ position: 'relative', zIndex: 1 }}>
              {item.locked ? (
                collapsed ? (
                  <Tooltip
                    title={`${item.label} — requires ${item.requiredPlan ?? 'Pro'} plan`}
                    placement="right"
                  >
                    <NavItem
                      to={resolvePath(item.path)}
                      aria-label={item.label}
                      sx={{
                        justifyContent: 'center',
                        mx: 0,
                        px: 0,
                        width: '100%',
                        minHeight: 36,
                      }}
                    >
                      {item.icon}
                    </NavItem>
                  </Tooltip>
                ) : (
                  <NavItem to={resolvePath(item.path)}>
                    {item.icon}
                    <LockedFeatureBadge planName={item.requiredPlan ?? 'Pro'}>
                      {item.label}
                    </LockedFeatureBadge>
                  </NavItem>
                )
              ) : !item.disabled && item.children ? (
                collapsed ? (
                  <Tooltip
                    title={
                      item.label === 'Bookings' && hasOvertimePending
                        ? `${item.label} — overtime pending`
                        : item.label
                    }
                    placement="right"
                  >
                    <NavItem
                      to={resolvePath(item.path)}
                      aria-label={item.label}
                      sx={{
                        justifyContent: 'center',
                        mx: 0,
                        px: 0,
                        width: '100%',
                        minHeight: 36,
                        position: 'relative',
                      }}
                    >
                      {item.icon}
                      {item.label === 'Bookings' && hasOvertimePending && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: '#EF4444',
                            border: '1.5px solid white',
                          }}
                        />
                      )}
                    </NavItem>
                  </Tooltip>
                ) : (
                  <>
                    <ExpandButton isActive={isParentActive(item)}>
                      {/* Label area: navigates to item.path */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flex: 1,
                          minWidth: 0,
                        }}
                        onClick={() => navigate(resolvePath(item.path))}
                      >
                        {item.icon}
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {item.label}
                          {/* Overtime pending dot on Bookings nav item */}
                          {item.label === 'Bookings' && hasOvertimePending && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#EF4444',
                                flexShrink: 0,
                                boxShadow: '0 0 0 2px rgba(239,68,68,0.25)',
                              }}
                              aria-label="Overtime decisions pending"
                            />
                          )}
                        </Box>
                      </Box>
                      {/* Chevron: only toggles expand/collapse */}
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(item.label);
                        }}
                        sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, p: 0.25 }}
                      >
                        {expandedItems.includes(item.label) ? (
                          <KeyboardArrowDown sx={{ fontSize: 18 }} />
                        ) : (
                          <KeyboardArrowRight sx={{ fontSize: 18 }} />
                        )}
                      </Box>
                    </ExpandButton>
                    <Collapse
                      in={expandedItems.includes(item.label)}
                      sx={{ position: 'relative', zIndex: 1 }}
                    >
                      {item.children.map((child) => (
                        <NavItem
                          key={child.path}
                          to={resolvePath(child.path)}
                          end={child.end}
                          nested
                        >
                          {({ isActive }) => (
                            <>
                              <Box
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: '50%',
                                  backgroundColor: isActive ? '#FF8800' : 'rgba(255,255,255,0.3)',
                                  flexShrink: 0,
                                }}
                              />
                              {child.label}
                            </>
                          )}
                        </NavItem>
                      ))}
                    </Collapse>
                  </>
                )
              ) : item.disabled ? (
                collapsed ? (
                  <Tooltip
                    title={`${item.label} - ${item.badge || 'Unavailable'}`}
                    placement="right"
                  >
                    <Box
                      aria-label={item.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: 36,
                        color: 'text.disabled',
                        cursor: 'default',
                        '& svg': { fontSize: 20, opacity: 0.5 },
                      }}
                    >
                      {item.icon}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2.5,
                      py: 1,
                      mr: 1.5,
                      borderRadius: 1,
                      color: 'text.disabled',
                      fontFamily: '"IBM Plex Sans", sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'default',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                    <Box sx={{ flex: 1 }}>{item.label}</Box>
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        }}
                      />
                    )}
                  </Box>
                )
              ) : collapsed ? (
                <Tooltip title={item.label} placement="right">
                  <NavItem
                    to={resolvePath(item.path)}
                    aria-label={item.label}
                    sx={{
                      justifyContent: 'center',
                      mx: 0,
                      px: 0,
                      width: '100%',
                      minHeight: 36,
                    }}
                  >
                    {item.icon}
                  </NavItem>
                </Tooltip>
              ) : (
                <NavItem to={resolvePath(item.path)}>
                  {item.icon}
                  {item.label}
                </NavItem>
              )}
            </Box>
          ))}
        </SidebarSection>
      ))}
      {/* Toggle collapse button */}
      <Box
        sx={{
          mt: 'auto',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
          p: 0.5,
        }}
      >
        <Tooltip
          title={collapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
          placement="right"
        >
          <IconButton
            onClick={onToggleCollapse}
            size="medium"
            aria-expanded={!collapsed}
            aria-label="Toggle sidebar"
            sx={{
              minHeight: 44,
              minWidth: 44,
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            {collapsed ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft />}
          </IconButton>
        </Tooltip>
      </Box>
    </SidebarContainer>
  );
}
